import { jsx as _jsx } from "react/jsx-runtime";
import { useUploadProgress } from '#pplx/frontend/apps/ask/connectors/hooks/useUploadProgress.js';
import Toast from '#pplx/frontend/components/overlays/Toast.js';
import { EVENT_PAYLOAD } from '#pplx/frontend/framework/instrumentation/types.js';
import { useInstrumentation } from '#pplx/frontend/framework/instrumentation/useInstrumentation.js';
import { $icon } from '#pplx/frontend/icons/icon-sprite.js';
import { logger } from '#pplx/frontend/logger/browser.js';
import { useRouter } from '#pplx/frontend/router/provider.js';
import { useIntl } from 'react-intl';
const uploadProgressToastSelectedEvent = EVENT_PAYLOAD;
export const UploadProgressToast = ({ fileRepoInfo, collectionSlug, onViewFiles, onClose, }) => {
    const reason = 'upload-progress-toast';
    const { $t } = useIntl();
    const router = useRouter();
    const { track } = useInstrumentation();
    const { total, pending, errors, projectOverSizeLimit, projectImportWorkLimitExceeded, isFinished, } = useUploadProgress({
        fileRepoInfo,
        reason,
        enablePolling: true,
    });
    const ctaText = $t({
        defaultMessage: 'See files',
        description: 'Call-to-action button label in the upload progress toast to view uploaded files',
        id: 'db9444dea1',
    });
    const navigateToFiles = onViewFiles ??
        (() => {
            switch (fileRepoInfo.file_repository_type) {
                case 'COLLECTION':
                    if (!collectionSlug) {
                        logger.error('Collection slug is not set', { fileRepoInfo });
                        return;
                    }
                    router.push(`/spaces/${collectionSlug}?showUploadModal`);
                    break;
                default:
                    logger.warn('Unsupported file repository type', { fileRepoInfo });
            }
        });
    const destination = onViewFiles
        ? 'originating_surface'
        : fileRepoInfo.file_repository_type === 'COLLECTION'
            ? 'collection_files'
            : 'unsupported';
    const ctaOnClick = () => {
        track(uploadProgressToastSelectedEvent({
            name: 'file upload progress toast selected',
            data: {
                fileRepositoryType: fileRepoInfo.file_repository_type,
                destination,
                phase: isFinished ? 'finished' : 'syncing',
            },
        }));
        navigateToFiles();
    };
    if (!total) {
        return null;
    }
    if (projectOverSizeLimit) {
        return (_jsx(Toast, { message: $t({
                defaultMessage: 'Project is over the size limit',
                description: 'Toast message shown when a connector import exceeds the project size limit',
                id: '795ac07af3',
            }), description: $t({
                defaultMessage: 'Remove files from the project and try again',
                description: 'Toast description explaining how to resolve a project size limit error',
                id: '4162924796',
            }), variant: "error", ctaText: ctaText, ctaOnClick: ctaOnClick, closeOnCtaClick: !!onViewFiles, isVisible: true, handleClose: onClose, timeout: null }));
    }
    if (projectImportWorkLimitExceeded) {
        return (_jsx(Toast, { message: $t({
                defaultMessage: 'Connector import is too large',
                description: 'Toast message shown when a connector import creates too much work',
                id: 'cf6c64157c',
            }), description: $t({
                defaultMessage: 'Try again with fewer folders.',
                description: 'Toast description explaining how to retry a partial connector import',
                id: '13ebdd3a20',
            }), variant: "error", ctaText: ctaText, ctaOnClick: ctaOnClick, closeOnCtaClick: !!onViewFiles, isVisible: true, handleClose: onClose, timeout: null }));
    }
    if (isFinished) {
        const message = $t({
            defaultMessage: '{total, plural, one {# file imported} other {# files imported}}',
            description: 'Toast message shown when file import is complete',
            id: '00896fe5ce',
        }, {
            total,
        });
        const description = $t({
            defaultMessage: 'Ready to search',
            description: 'Toast description shown when file sync is complete and files are searchable',
            id: '09546769cb',
        });
        return (_jsx(Toast, { message: message, description: description, variant: "success", iconOverride: $icon('circle-check'), ctaText: ctaText, ctaOnClick: ctaOnClick, isVisible: true, handleClose: onClose, onTimeout: onClose, timeout: 5 }));
    }
    const percentage = Math.round(((total - pending) / total) * 100);
    const message = $t({
        defaultMessage: 'Importing {total, plural, one {# file} other {# files}}',
        description: 'Toast message shown while files are being imported',
        id: '9b74f9819a',
    }, {
        total,
    });
    const description = isNaN(percentage)
        ? ''
        : errors === 0
            ? $t({
                defaultMessage: '{percentage, number, ::percent} complete',
                description: 'Toast description showing file sync progress percentage',
                id: '1a4f3f27eb',
            }, { percentage: percentage / 100 })
            : $t({
                defaultMessage: '{percentage, number, ::percent} complete with {errors, plural, one {# error} other {# errors}}',
                description: 'Toast description showing file sync progress percentage with error count',
                id: '2044586f22',
            }, { percentage: percentage / 100, errors });
    return (_jsx(Toast, { message: message, description: description, variant: "success", iconOverride: $icon('refresh'), animateIcon: true, "aria-busy": true, ctaText: ctaText, ctaOnClick: ctaOnClick, closeOnCtaClick: !!onViewFiles, isVisible: true, handleClose: onClose, timeout: null }));
};
//# sourceMappingURL=UploadProgressToast.js.map